import type { Page, Pageable } from '@/core/api/spring-types';
import { normalizePage } from '@/core/api/spring-types';
import type {
    TicketTierDto,
    TicketRedemptionDto,
    SchoolBalanceDto,
    TicketRedemptionStatusUpdateDto,
} from '@/api/generated/types.gen';
import {
    getTicketTiers,
    createTicketTier,
    updateTicketTier,
    deleteTicketTier,
    getOwnSchoolPointsBalance,
    getSchoolPointsBalance,
    redeemTicket,
    getOwnTicketRedemptions,
    getTicketRedemptions,
    updateTicketRedemptionStatus,
} from '@/api/generated/sdk.gen';
import { handleResponse } from '@/core/api/response-handler';

export type { TicketTierDto, TicketRedemptionDto, SchoolBalanceDto, TicketRedemptionStatusUpdateDto };
export type TicketRedemptionStatus = NonNullable<TicketRedemptionDto['status']>;

/**
 * Tichete de puncte pentru scoli (spec §8) - wrapper subtire peste SDK-ul generat.
 *
 * 🔴 FIECARE apel trece prin handleResponse(...). SDK-ul generat are ThrowOnError = false, deci
 * intoarce eroarea in loc sa o arunce: un `await someSdkFn(...)` fara handleResponse merge mai
 * departe ca si cum ar fi reusit, chiar la 4xx/5xx. A produs un bug real in sub-planul 05
 * (revocarea unui link raporta succes, linkul ramanea activ). Zero exceptii aici.
 *
 * ⚠️ Timestamp-urile ajung string-uri ISO, desi tipul generat spune Date - transformers.gen.ts
 * exista dar nu ruleaza niciodata. Formateaza cu formatUnixTimestamp(), nu cu .getTime().
 */
class TicketsAPI {
    /** Fara editionId, backendul foloseste editia curenta. */
    async getTiers(editionId?: string): Promise<TicketTierDto[]> {
        const response = await getTicketTiers({ query: editionId ? { editionId } : undefined });
        return handleResponse(response);
    }

    // `TicketTierController.create/update` raspund `ResponseEntity<Object>` in backend (acelasi
    // handler mai poate intoarce si un 409 cu alt corp), deci tipul generat pentru raspunsul de
    // succes e `{ [key: string]: unknown }`, nu `TicketTierDto` -- backendul chiar intoarce
    // DTO-ul, doar ca semnatura nu-l descrie. Cast, nu o schimbare de comportament.
    async createTier(dto: TicketTierDto): Promise<TicketTierDto> {
        return handleResponse(await createTicketTier({ body: dto })) as TicketTierDto;
    }

    async updateTier(dto: TicketTierDto): Promise<TicketTierDto> {
        return handleResponse(await updateTicketTier({ body: dto })) as TicketTierDto;
    }

    async deleteTier(id: string): Promise<void> {
        handleResponse(await deleteTicketTier({ path: { id } }));
    }

    /**
     * Soldul propriei scoli. Ruta /own exista pentru ca schoolId NU e in UserInfo pe frontend
     * (auth-provider-v2.tsx) - scoala se ia din claim, in backend.
     */
    async getOwnBalance(): Promise<SchoolBalanceDto> {
        return handleResponse(await getOwnSchoolPointsBalance());
    }

    async getBalance(schoolId: string): Promise<SchoolBalanceDto> {
        return handleResponse(await getSchoolPointsBalance({ path: { id: schoolId } }));
    }

    async redeem(tierId: string, alegereProfesor?: string): Promise<TicketRedemptionDto> {
        return handleResponse(await redeemTicket({ body: { tierId, alegereProfesor } }));
    }

    async getOwnRedemptions(): Promise<TicketRedemptionDto[]> {
        return handleResponse(await getOwnTicketRedemptions());
    }

    async getRedemptions(
        filter: { editionId?: string; status?: TicketRedemptionStatus },
        pageable: Pageable,
    ): Promise<Page<TicketRedemptionDto>> {
        // `as any` deliberat, cum fac toate celelalte 5 rapoarte paginate (schools.api.ts:39,
        // reports.api.ts, users.api.ts): tipul GENERAT declara `pageable: Pageable` imbricat, dar
        // pe sarma Spring leaga `page`/`size`/`sort` PLATE (PageableHandlerMethodArgumentResolver).
        // Un obiect imbricat ar serializa `pageable.page=0` si backendul ar cadea pe default-uri
        // tacut. Tipul minte despre formatul de pe sarma; precedentul e runtime-ul, nu tipul.
        const response = await getTicketRedemptions({ query: { ...filter, ...pageable } as any });
        return normalizePage(handleResponse(response));
    }

    /** note e OBLIGATORIU cand status = CANCELLED: backendul refuza cu 400 (ticket.note.required). */
    async updateStatus(id: string, body: TicketRedemptionStatusUpdateDto): Promise<TicketRedemptionDto> {
        return handleResponse(await updateTicketRedemptionStatus({ path: { id }, body }));
    }
}

export const ticketsApi = new TicketsAPI();
